import React, { useEffect, useRef } from 'react'
import chatSocketService from 'services/ChatSocketService'
import chatServices from 'services/ChatServices'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

const ROOM_TYPES = ['direct', 'group', 'document_thread']

const toDisplayName = (user = {}) => {
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim()
  return fullName || user?.full_name || user?.email || ''
}

/**
 * Mounts above all authenticated routes so the socket connection and browser
 * notifications stay alive regardless of which page is currently open.
 */
const ChatNotificationProvider = ({ children }) => {
  const joinedRoomIdsRef = useRef(new Set())
  const currentUser = useSelector((state) => state.auth.user) || {}

  const ensureNotificationPermission = async () => {
    if (!('Notification' in window)) {
      return 'denied'
    }

    if (Notification.permission === 'granted') {
      return 'granted'
    }

    if (Notification.permission === 'default') {
      try {
        return await Notification.requestPermission()
      } catch {
        return 'denied'
      }
    }

    return Notification.permission
  }

  // Ask for permission once on mount
  useEffect(() => {
    void ensureNotificationPermission()
  }, [])

  useEffect(() => {
    const currentUserId = String(currentUser?.id || currentUser?.user_id || '')
    let mounted = true

    const showNotification = async (title, body, roomId) => {
      const permission = await ensureNotificationPermission()
      if (permission !== 'granted') return

      const notification = new Notification(title, {
        body,
        icon: '/assets/siteLogo/logo.png',
        // One slot per room — replaces previous pending notification
        tag: `codex-chat-${roomId}`,
        renotify: true,
      })

      notification.onclick = () => {
        window.focus()
        window.location.href = '/collaboration'
        notification.close()
      }
    }

    const showInAppNotification = (title, body, roomId) => {
      toast.info(`${title}: ${body}`, {
        autoClose: 4500,
        onClick: () => {
          window.location.href = '/collaboration'
        },
        toastId: `chat-room-${roomId}-${Date.now()}`,
      })
    }

    const joinAllRooms = async () => {
      const roomResponses = await Promise.all(
        ROOM_TYPES.map((roomType) =>
          chatServices.getRooms({ room_type: roomType, page: 0, limit: 200 })
        )
      )

      if (!mounted) return

      roomResponses.forEach((response) => {
        if (!response.success) return
        const rows =
          response?.data?.data?.data ||
          response?.data?.view?.data ||
          response?.data?.data ||
          []
        rows.forEach((room) => {
          const id = String(room?.id)
          if (room?.id && !joinedRoomIdsRef.current.has(id)) {
            chatSocketService.joinRoom(room.id)
            joinedRoomIdsRef.current.add(id)
          }
        })
      })
    }

    const onNewMessage = (incoming) => {
      if (!incoming?.id || !incoming?.room_id) return
      // Skip own messages
      if (String(incoming?.sender_user_id || incoming?.sender?.id || '') === currentUserId) return

      const senderName = toDisplayName(incoming?.sender) || 'New message'
      const body = incoming?.message || 'Sent an attachment'

      // Always show an in-app toast so users get alerts even on the active tab.
      showInAppNotification(senderName, body, incoming.room_id)

      // Show browser notifications whenever permission is granted for incoming messages from other users.
      void showNotification(senderName, body, incoming.room_id)
    }

    const onConnect = () => {
      joinAllRooms()
    }

    const bindSocket = async () => {
      try {
        await chatSocketService.connect()
        if (!mounted) return

        onConnect()
        chatSocketService.on('connect', onConnect)
        chatSocketService.on('new_message', onNewMessage)
      } catch {
        if (mounted) {
          toast.error('Chat notifications are temporarily unavailable.')
        }
      }
    }

    bindSocket()

    return () => {
      mounted = false
      chatSocketService.off('connect', onConnect)
      chatSocketService.off('new_message', onNewMessage)
      joinedRoomIdsRef.current.forEach((roomId) => chatSocketService.leaveRoom(roomId))
      joinedRoomIdsRef.current.clear()
      chatSocketService.disconnect()
    }
  }, [])

  return children
}

export default ChatNotificationProvider
